<script setup>
/**
 * This component is the form to just edit the properties of a model.
 */

import { useAuthStore } from '@/Stores/auth'
const auth = useAuthStore()

const props = defineProps({
    organisation: {
        type: Object,
        default: () => ({}),
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
    app: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits([
	'update:organisation',
])
</script>

<template>
    <form @submit.stop.prevent="emit('update:organisation')" class="flex flex-col h-full grow">
		<input type="submit" class="hidden" />
        <Input 
            placeholder="eg. Advance Cairns" 
            v-model="organisation.name" 
            :errors="errors.name" 
            :autofocus="true" 
            label="Organisation Name"
            testid="name"	
        />

        <div class="grid grid-cols-1 xl:grid-cols-3 gap-x-4">
            <div>
                <Input 
                    placeholder="eg. https://www.advancecairns.com.au" 
                    v-model="organisation.website" 
                    :errors="errors.website" 
                    label="Website"
                    testid="website"
					type="website"
                />
        	</div>
        	<div>
            	<Input 
					placeholder="eg. hello@advancecairns.com.au" 
					v-model="organisation.email" 
					:errors="errors.email" 
					label="Best Email"
					testid="email"
					type="email"
				/>
       		</div>
            <div>
                <Input 
                    placeholder="eg. +61 4 1234 5678" 
                    v-model="organisation.phone" 
                    :errors="errors.phone" 
                    label="Best Phone"
                    testid="phone"
					type="phone"
                />
        	</div>
    	</div>

        <div class="flex flex-row gap-x-4">
            <div class="w-2/4">
                <Input 
                    placeholder="eg. 012345678" 
                    v-model="organisation.abn" 
                    :errors="errors.abn" 
                    label="ABN"
                    testid="abn"
					type="abn"
                />
            </div>
            <div v-if="auth.isAdmin" class="w-2/4">
                <Input 
                    placeholder="eg. Advance Cairns" 
                    v-model="organisation.joined_date" 
                    :errors="errors.joined_date" 
                    type="date"
                    label="Joined date"
                    help-text="The date the organisation's membership commenced."
                    testid="joined_date"
                />
            </div>
        </div>

        <div v-if="auth.isAdmin">
			<div class="flex flex-row gap-x-4">
				<div class="w-1/3">
					<Input 
						label="Membership Level"
						type="select"
						:options="app.membership_levels"
						v-model="organisation.membership_level" 
						:errors="errors.membership_level" 
						testid="membership_level"
					/>
				</div>
				<div class="w-1/3">
					<Input 
						label="Payment Frequency"
						type="select"
						v-model="organisation.payment_frequency"
						:options="app.payment_frequencies"
						:errors="errors.payment_frequency" 
						testid="payment_frequency"
					/>
				</div>
				<div class="w-1/3">
					<Input 
						label="Membership Status"
						type="select"
						v-model="organisation.membership_status"
						:options="app.membership_statuses"
						:errors="errors.membership_status" 
						testid="membership_status"
					/>
				</div>
			</div>
			<div class="flex flex-row gap-x-4">
				<div class="w-1/2">
					<Input 
						label="Membership Expiry Date"
						type="date"
						v-model="organisation.membership_expires_at"
						:errors="errors.membership_expires_at" 
						testid="membership_expires_at"
					/>
				</div>
				<div class="w-1/2">
					<Input 
						label="Membership Fee"
						type="currency"
						v-model="organisation.membership_fee"
						:errors="errors.membership_fee" 
						testid="membership_fee"
					/>
				</div>
        	</div>
        </div>
	
        <div class="flex flex-row gap-x-4">
            <div class="w-full">
                <Input 
                    placeholder="eg. 123 Main St" 
                    v-model="organisation.street_address" 
                    :errors="errors.street_address" 
                    label="Street Address"
                    testid="street_address"
                />
            </div>
        </div>

        <div class="flex flex-row gap-x-4">
            <div class="w-1/3">
                <Input 
                    placeholder="eg. Sydney" 
                    v-model="organisation.city" 
                    :errors="errors.city" 
                    label="City"
                    testid="city"
                />
            </div>
            <div class="w-1/3">
                <Input 
                    placeholder="eg. QLD" 
                    v-model="organisation.state" 
                    :errors="errors.state" 
                    label="State"
                    testid="state"
					type="state"
                />
            </div>
            <div class="w-1/3">
                <Input 
                    placeholder="eg. 2000" 
                    v-model="organisation.postal_code" 
                    :errors="errors.postal_code" 
                    label="Postal Code"
                    testid="postal_code"
					type="post_code"
                />
            </div>
        </div>

        <div  v-if="auth.isAdmin" class="md:flex md:flex-row gap-x-4">
            <div class="w-full">
                <Input 
                    placeholder="eg. 1247c27b-b6a1-4987-8271-ff7c0ae154e" 
                    v-model="organisation.xero_id" 
                    :errors="errors.xero_id" 
                    label="Xero ID"
                    testid="xero_id"
                    help-text="You can find this number by editing the contact in Xero and getting the contactID from the url bar (look for '?contactID=')"
					type="xero_id"
                />
            </div>
        </div>
        
    </form>
</template>